import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IconBell } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getNotifications, readAllNotifications } from "store/slices/dashboard";
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";
import NotificationList from "./NotificationList";

const NotificationSection = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { isLoading, data } = useSelector(
    (state) => state.dashboard.notifications
  );
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const fetchLastNotifications = () => {
      dispatch(getNotifications());
    };
    fetchLastNotifications();
    const intervalId = setInterval(fetchLastNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [dispatch]);
  const anchorRef = useRef(null);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleReadAllNotifications = () => {
    dispatch(readAllNotifications());
  };

  const notReadNbs = data.filter((item) => !item.read).length;

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Box
        sx={{
          ml: 2,
          mr: 3,
          [theme.breakpoints.down("md")]: {
            mr: 2,
          },
        }}
      >
        <Badge
          badgeContent={notReadNbs}
          max={20}
          showZero={false}
          color="error"
        >
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: "all .2s ease-in-out",
              background: theme.palette.primary.light,
              color: theme.palette.primary.dark,
              '&[aria-controls="menu-list-grow"],&:hover': {
                background: theme.palette.primary.dark,
                color: theme.palette.primary.light,
              },
            }}
            ref={anchorRef}
            aria-controls={open ? "menu-list-grow" : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            color="inherit"
          >
            <IconBell stroke={1.5} size="1.3rem" />
          </Avatar>
        </Badge>
      </Box>

      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [matchesXs ? 5 : 0, 20],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions
              position={matchesXs ? "top" : "top-right"}
              in={open}
              {...TransitionProps}
            >
              <Paper>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    {isLoading ? (
                      <CircularProgress />
                    ) : (
                      <Grid container direction="column" spacing={2}>
                        <Grid item xs={12}>
                          <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ pt: 2, px: 2 }}
                          >
                            <Grid item>
                              <Stack direction="row" spacing={2}>
                                <Typography variant="subtitle1">
                                  <FormattedMessage id="notifications.title" />
                                </Typography>
                                <Chip
                                  size="small"
                                  label={notReadNbs}
                                  sx={{
                                    color: theme.palette.background.default,
                                    bgcolor: theme.palette.warning.dark,
                                  }}
                                />
                              </Stack>
                            </Grid>
                            <Grid item>
                              <Typography
                                component={Link}
                                onClick={handleReadAllNotifications}
                                variant="subtitle2"
                                color="primary"
                              >
                                <FormattedMessage id="notifications.read-all" />
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <PerfectScrollbar
                            style={{
                              height: "100%",
                              maxHeight: "calc(100vh - 205px)",
                              overflowX: "hidden",
                            }}
                          >
                            <Grid container direction="column" spacing={2}>
                              <Grid item xs={12} p={0}>
                                <Divider sx={{ my: 0 }} />
                              </Grid>
                            </Grid>
                            {data && <NotificationList data={data} />}
                          </PerfectScrollbar>
                        </Grid>
                      </Grid>
                    )}
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default NotificationSection;
